import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { alpha, CircularProgress, IconButton, Tooltip, useTheme } from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import type { ConversationTurn, UUID } from '@/index'
import { standardTooltipProps } from '@/common/components'
import { useConversationControlsState } from '../hooks'

interface InjectUserInputControlProps {
  readonly conversationId: UUID
  readonly sessionId: UUID
  readonly conversationTurn: ConversationTurn | undefined
  readonly onInject: () => void
}

export const InjectUserInputControl: FC<InjectUserInputControlProps> = ({
  conversationId,
  sessionId,
  conversationTurn,
  onInject,
}) => {
  const theme = useTheme()
  const { canInject, isSubmitting } = useConversationControlsState(
    conversationId,
    sessionId,
    conversationTurn,
  )

  if (canInject) {
    return (
      <Tooltip {...standardTooltipProps} title="Inject Prompt">
        <span>
          <IconButton
            onClick={onInject}
            disabled={isSubmitting}
            size="small"
            sx={injectButtonSx(theme)}
          >
            {isSubmitting ? (
              <CircularProgress size={16} sx={loadingSpinnerSx} />
            ) : (
              <AddIcon sx={controlIconSx} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    )
  }

  return null
}

const injectButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.info.main,
  padding: '4px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: alpha(theme.palette.info.main, 0.08),
  },
})

const controlIconSx: SxProps<Theme> = {
  fontSize: '1.3rem',
}

const loadingSpinnerSx: SxProps<Theme> = {
  color: 'inherit',
}
